import { useState } from 'react';
import { CalendarClock, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import {
  chargeMessage,
  chargeWhatsappLink,
  formatMoney,
  isUsablePhone,
  type ChargeGroup,
} from '@/lib/overdueCharges';

interface OverdueChargesProps {
  groups: ChargeGroup[];
  onCharged: () => void;
}

const formatDate = (iso: string) => new Date(iso).toLocaleDateString('pt-BR');

export const OverdueCharges = ({ groups, onCharged }: OverdueChargesProps) => {
  const { toast } = useToast();
  const [active, setActive] = useState<ChargeGroup | null>(null);
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');

  const open = (group: ChargeGroup) => {
    setActive(group);
    setPhone(group.phone);
    setMessage(chargeMessage(group));
  };

  // O link precisa abrir no clique, antes das gravações, para o navegador não bloquear a nova aba
  const send = async () => {
    if (!active) return;
    const group = active;
    window.open(chargeWhatsappLink(phone, message), '_blank', 'noopener,noreferrer');
    setActive(null);

    try {
      const updates: PromiseLike<{ error: unknown }>[] = [
        (supabase as any).from('financial_entries').update({ charge_sent_at: new Date().toISOString() }).in('id', group.entries.map((e) => e.id)),
      ];
      // Número digitado aqui fica no cadastro de quem é cliente e ainda não tinha telefone
      if (group.clientId && !group.phone) updates.push((supabase as any).from('clients').update({ mobile: phone.trim() }).eq('id', group.clientId));
      const results = await Promise.all(updates);
      const failed = results.find((r) => r.error);
      if (failed) throw failed.error;
      onCharged();
    } catch {
      toast({ title: 'O WhatsApp abriu, mas a data da cobrança não foi salva', variant: 'destructive' });
    }
  };

  if (groups.length === 0) {
    return (
      <div className="px-6 py-12 text-center">
        <p className="text-sm font-medium text-slate-500">Nenhum valor em atraso</p>
      </div>
    );
  }

  return (
    <>
      <ul className="divide-y divide-slate-100">
        {groups.map((group) => (
          <li key={group.key} className="flex flex-col gap-3 px-5 py-4 xl:flex-row xl:items-center xl:justify-between xl:px-6">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <p className="text-[15px] font-semibold text-slate-900">{group.name}</p>
                <p className="text-sm font-semibold tabular-nums text-red-600">{formatMoney(group.total)}</p>
              </div>
              <ul className="mt-1 space-y-0.5 text-xs text-slate-500">
                {group.entries.map((entry) => (
                  <li key={entry.id} className="break-words">{String(entry.description || 'Honorários').replace(/\s+/g, ' ').trim()}</li>
                ))}
              </ul>
              <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
                <span className="font-medium text-red-600">{group.daysLate} {group.daysLate === 1 ? 'dia' : 'dias'} em atraso</span>
                {group.lastChargeAt && (
                  <span className="inline-flex items-center gap-1 text-slate-500"><CalendarClock className="h-3 w-3" />WhatsApp aberto em {formatDate(group.lastChargeAt)}</span>
                )}
                {!group.phone && <span className="text-amber-700">Sem telefone</span>}
              </p>
            </div>
            <Button size="sm" onClick={() => open(group)} className="h-9 shrink-0 rounded-xl bg-slate-900 text-[13px] text-white hover:bg-slate-800">
              <MessageCircle className="mr-1.5 h-4 w-4 text-[#e8d5a8]" />
              {group.lastChargeAt ? 'Cobrar de novo' : 'Cobrar'}
            </Button>
          </li>
        ))}
      </ul>

      <Dialog open={!!active} onOpenChange={(isOpen) => !isOpen && setActive(null)}>
        <DialogContent className="w-[95vw] max-w-lg rounded-2xl bg-white">
          <DialogHeader>
            <DialogTitle>Cobrar {active?.name}</DialogTitle>
            <DialogDescription>Revise a mensagem. Ela abre no WhatsApp, e o envio é feito por você.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="charge-phone">WhatsApp</Label>
              <Input id="charge-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(81) 99999-9999" />
              {active?.clientId && !active.phone && <p className="text-xs text-slate-500">O número ficará salvo no cadastro do cliente.</p>}
              {phone && !isUsablePhone(phone) && <p className="text-xs text-red-600">Informe o número com DDD.</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="charge-message">Mensagem</Label>
              <Textarea id="charge-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={10} className="resize-y text-sm" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setActive(null)}>Cancelar</Button>
            <Button onClick={send} disabled={!isUsablePhone(phone) || !message.trim()} className="bg-slate-900 text-white hover:bg-slate-800">
              <MessageCircle className="mr-1.5 h-4 w-4 text-[#e8d5a8]" />Abrir no WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
